import React, { useState, useRef } from 'react';
import { parseCalibrationExcel } from '../../utils/ExcelParser';
import { ExcelLayouts } from '../../settings/ExcelLayouts';

const ImportarCalibracionModal = ({ isOpen, onClose, onSave }) => {
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);

    // dataList: Array de { file, status: 'pending'|'success'|'error', data: {head, details}, errorMsg }
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Format Selection
    const [selectedFormat, setSelectedFormat] = useState('MASTER_METER_V1');

    // Para ver detalle de uno específico
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(null);

    if (!isOpen) return null;

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        await processFiles(files);
        e.target.value = null; // Allow re-select
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result); // Returns data:....base64,....
            reader.onerror = error => reject(error);
        });
    };

    const processFiles = async (files) => {
        if (files.length === 0) return;
        setLoading(true);

        const initialList = dataList.length > 0 ? [...dataList] : [];
        const newResults = [];

        for (const file of files) {
            if (!file.name.match(/\.(xls|xlsx)$/i)) continue;

            try {
                let parsedData;
                if (selectedFormat === 'INTELIGENCIA_ARTIFICIAL') {
                    const { parseExcelWithAI } = await import('../../utils/AIExtractor');
                    parsedData = await parseExcelWithAI(file);
                } else {
                    parsedData = await parseCalibrationExcel(file, selectedFormat);
                }

                if (!parsedData.details || parsedData.details.length === 0) {
                    newResults.push({
                        file,
                        status: 'error',
                        errorMsg: 'El archivo no contiene detalles válidos o formato incorrecto.'
                    });
                } else {
                    // Convertir archivo a binario
                    const base64 = await fileToBase64(file);
                    parsedData.head.File_Doc = base64;
                    parsedData.head.FileName_Doc = file.name;
                    parsedData.head.FileName_Ruta = file.name;

                    // Default values for missing columns
                    parsedData.head.Clasificacion = 'CALIBRACION';
                    parsedData.head.AuxCalibracion = '';
                    parsedData.head.EstadoFormato = 1;

                    newResults.push({
                        file,
                        status: 'success',
                        data: parsedData
                    });
                }
            } catch (err) {
                console.error(err);
                newResults.push({
                    file,
                    status: 'error',
                    errorMsg: 'Error al leer el archivo. Posible formato inválido.'
                });
            }
        }

        setDataList([...initialList, ...newResults]);
        setLoading(false);
    };

    const handleGuardarTodos = () => {
        const validItems = dataList.filter(item => item.status === 'success').map(item => item.data);
        if (validItems.length > 0) {
            onSave(validItems);
            // Don't close immediately if there are errors? Or yes?
            // Usually close.
            onClose();
        }
    };

    const validCount = dataList.filter(i => i.status === 'success').length;
    const errorCount = dataList.filter(i => i.status === 'error').length;

    const previewItem = selectedPreviewIndex !== null ? dataList[selectedPreviewIndex] : null;

    // Reset list handler
    const clearList = () => {
        setDataList([]);
        setSelectedPreviewIndex(null);
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* HIDDEN INPUTS (Fixed Crash: Always rendered) */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    multiple
                    accept=".xlsx, .xls"
                />
                <input
                    type="file"
                    ref={folderInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    webkitdirectory=""
                    directory=""
                    multiple
                />

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        Importación de Calibraciones
                    </h3>
                    <div className="flex items-center gap-4">
                        <select
                            value={selectedFormat}
                            onChange={(e) => setSelectedFormat(e.target.value)}
                            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
                        >
                            {Object.entries(ExcelLayouts).map(([key, config]) => (
                                <option key={key} value={key}>{config.label}</option>
                            ))}
                            <option value="INTELIGENCIA_ARTIFICIAL">✨ Extracción con IA (Formatos Variables)</option>
                        </select>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 flex flex-col md:flex-row gap-6">

                    {/* LEFT PANEL: Controls & List */}
                    <div className="flex-1 flex flex-col gap-4 min-w-[300px]">

                        {/* Control Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => fileInputRef.current.click()}
                                className="flex-1 py-3 px-4 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 hover:bg-indigo-100 font-medium flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                Archivos
                            </button>
                            <button
                                onClick={() => folderInputRef.current.click()}
                                className="flex-1 py-3 px-4 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100 font-medium flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                                Carpeta
                            </button>
                        </div>

                        {dataList.length === 0 && !loading && (
                            <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-500 p-8 text-center">
                                <p>Seleccione archivos individuales o una carpeta completa para comenzar el procesamiento.</p>
                                <p className="text-xs mt-2 text-gray-400">Formato seleccionado: {selectedFormat.replace(/_/g, " ")}</p>
                            </div>
                        )}

                        {loading && (
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                <span className="ml-3 text-gray-600">Procesando...</span>
                            </div>
                        )}

                        {dataList.length > 0 && (
                            <div className="flex flex-col flex-1 min-h-0">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-sm font-bold text-gray-700">Archivos ({dataList.length})</h4>
                                    <button onClick={clearList} className="text-xs text-red-500 hover:underline">Limpiar Lista</button>
                                </div>
                                <div className="flex-1 overflow-y-auto border rounded-lg bg-white">
                                    {dataList.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => item.status === 'success' && setSelectedPreviewIndex(idx)}
                                            className={`p-3 border-b last:border-b-0 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors
                                                ${selectedPreviewIndex === idx ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''}`}
                                        >
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-medium text-gray-800 truncate" title={item.file.name}>{item.file.name}</span>
                                                    <span className="text-xs text-gray-500">
                                                        {item.status === 'success'
                                                            ? `${item.data.details.length} registros`
                                                            : item.errorMsg}
                                                    </span>
                                                </div>
                                            </div>
                                            {item.status === 'success' && (
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2 text-xs text-gray-500 flex justify-between font-medium">
                                    <span className="text-green-600">Válidos: {validCount}</span>
                                    <span className="text-red-600">Errores: {errorCount}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT PANEL: Preview */}
                    <div className="flex-[2] border-l border-gray-100 pl-6 flex flex-col min-w-0">
                        <h4 className="text-sm font-bold text-gray-700 mb-2">Vista Previa</h4>
                        {previewItem && previewItem.status === 'success' ? (
                            <div className="flex-1 overflow-y-auto pr-2">
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 text-xs">
                                        <div><span className="text-gray-500">Compañía:</span> <span className="font-semibold block truncate">{previewItem.data.head.Compania}</span></div>
                                        <div><span className="text-gray-500">Planta:</span> <span className="font-semibold block truncate">{previewItem.data.head.Planta}</span></div>
                                        <div><span className="text-gray-500">Fecha:</span> <span className="font-semibold block">{previewItem.data.head.Fecha?.toLocaleDateString()}</span></div>
                                        <div><span className="text-gray-500">Mesa:</span> <span className="font-semibold block truncate">{previewItem.data.head.MesaCarga}</span></div>
                                        <div><span className="text-gray-500">Brazo:</span> <span className="font-semibold block truncate">{previewItem.data.head.Brazo}</span></div>
                                        <div><span className="text-gray-500">Producto:</span> <span className="font-semibold block truncate">{previewItem.data.head.Producto}</span></div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Carga</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Err Bruto</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Err Neto</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Dif Bruto</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Dif Neto</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {previewItem.data.details.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-3 py-1 text-xs">{row.carga}</td>
                                                    <td className="px-3 py-1 text-xs text-right">
                                                        <input 
                                                            type="number" 
                                                            step="0.0001" 
                                                            className={`w-20 text-right bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 ${Math.abs(row.ErrorVolumenBruto) > 0.5 ? 'text-red-600 font-bold' : ''}`}
                                                            value={row.ErrorVolumenBruto || 0}
                                                            onChange={(e) => {
                                                                const newList = [...dataList];
                                                                newList[selectedPreviewIndex].data.details[idx].ErrorVolumenBruto = parseFloat(e.target.value) || 0;
                                                                setDataList(newList);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-1 text-xs text-right">
                                                        <input 
                                                            type="number" 
                                                            step="0.0001" 
                                                            className="w-20 text-right bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                                                            value={row.ErrorVolumenNeto || 0}
                                                            onChange={(e) => {
                                                                const newList = [...dataList];
                                                                newList[selectedPreviewIndex].data.details[idx].ErrorVolumenNeto = parseFloat(e.target.value) || 0;
                                                                setDataList(newList);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-1 text-xs text-right">
                                                        <input 
                                                            type="number" 
                                                            step="0.01" 
                                                            className="w-20 text-right bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                                                            value={row.DifVolBruto || 0}
                                                            onChange={(e) => {
                                                                const newList = [...dataList];
                                                                newList[selectedPreviewIndex].data.details[idx].DifVolBruto = parseFloat(e.target.value) || 0;
                                                                setDataList(newList);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-1 text-xs text-right">
                                                        <input 
                                                            type="number" 
                                                            step="0.01" 
                                                            className="w-20 text-right bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                                                            value={row.DifVolNeto || 0}
                                                            onChange={(e) => {
                                                                const newList = [...dataList];
                                                                newList[selectedPreviewIndex].data.details[idx].DifVolNeto = parseFloat(e.target.value) || 0;
                                                                setDataList(newList);
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                <svg className="w-12 h-12 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                <span className="text-sm text-center px-4">Seleccione un archivo de la lista de la izquierda para ver su contenido</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleGuardarTodos}
                        disabled={validCount === 0}
                        className={`px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-colors flex items-center gap-2
                            ${validCount === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                        Guardar {validCount > 0 ? `(${validCount}) Registros` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportarCalibracionModal;
